<template>
    <div class="basic-plan-city-select">
        <mc-level-select
            v-model="innerValue"
            :data="innerData"
            :tab-config="innerTabConfig"
            :multiple="multiple"
            :placeholder="placeholder"
            :filter-placeholder="filterPlaceholder"
            :value-key="valueKey"
            :show-count="showCount"
            :show-tags="showTags"
            :show-select-all="showSelectAll"
            :show-select-tab-all="showSelectTabAll"
            :disabled="disabled"
            :append-to-container="appendToContainer"
            show-selected-view-default
            :show-footer="false"
            v-bind="$attrs"
            @change="handleChange"
        />
    </div>
</template>

<script>
import {
    ref, computed, watch,
} from '@vue/composition-api';

/**
 * 基盘城市选择器组件
 * 用于展示 区域 - 经营城市的两层级城市选择
 * 支持按城市状态（全部城市、正常经营、筹建中、测试）进行筛选
 *
 * 使用示例：
 * <BasicPlanCitySelect
 *   v-model="selectedCities"
 *   :data="cityData"
 *   :disabled-city-ids="[1, 2, 3]"
 *   multiple
 * />
 */
export default {
    name: 'BasicPlanCitySelect',
    props: {
        // 选中的值，为经营城市ID的数组
        value: {
            type: [Array, String, Number],
            default: () => [],
        },
        // 是否多选
        multiple: {
            type: Boolean,
            default: true,
        },
        // 城市数据（必传）
        data: {
            type: Array,
            default: () => [],
        },
        // 是否展示已选项的标签
        showTags: {
            type: [Boolean, Number],
            default: false,
        },
        // 是否展示选择数量
        showCount: {
            type: Boolean,
            default: true,
        },
        // 是否显示全选选项
        showSelectAll: {
            type: Boolean,
            default: true,
        },
        // 是否显示标签全选选项
        showSelectTabAll: {
            type: Boolean,
            default: true,
        },
        // 占位符
        placeholder: {
            type: String,
            default: '请选择城市',
        },
        // 搜索占位符
        filterPlaceholder: {
            type: String,
            default: '请搜索',
        },
        // 值的key字段（经营城市ID）
        valueKey: {
            type: String,
            default: 'id',
        },
        // 禁用的城市ID列表
        disabledCityIds: {
            type: Array,
            default: () => [],
        },
        // 是否禁用整个组件
        disabled: {
            type: Boolean,
            default: false,
        },
        // option-city 相关属性
        existedIds: {
            type: Array,
            default: () => [],
        },
        publicIds: {
            type: Array,
            default: () => [],
        },
        selectedIds: {
            type: Array,
            default: () => [],
        },
        uuid: {
            type: String,
            default: '',
        },
        title: {
            type: String,
            default: '',
        },
        preSkuId: {
            type: String,
            default: '',
        },
        isInformationSink: {
            type: Boolean,
            default: false,
        },
        isPbCityDisabledIds: {
            type: Array,
            default: () => [],
        },
        guaranteePeriodMap: {
            type: [Object, Map],
            default: () => ({}),
        },
        appendToContainer: {
            type: Boolean,
            default: true,
        },
    },
    setup(props, { emit }) {
        const innerValue = ref(props.value);

        // optionCity 兼容：合并已存在城市和已选择城市
        const itemSelectedCityIds = computed(() => [...new Set([...(props.existedIds || []), ...(props.selectedIds || [])])]);

        /**
         * 获取保质期规则的辅助函数（支持 Object 和 Map）
         */
        const getGuaranteePeriodRule = (cityId) => {
            if (!props.guaranteePeriodMap) return undefined;
            if (props.guaranteePeriodMap instanceof Map) {
                return props.guaranteePeriodMap.get(cityId);
            }
            return props.guaranteePeriodMap[cityId];
        };

        /**
         * 保质期一致性检查：判断城市是否因保质期不一致而被禁用
         */
        const isCityDisabledByGuaranteePeriod = (cityId) => {
            // 如果没有已选城市，不禁用任何城市
            if (!itemSelectedCityIds.value || itemSelectedCityIds.value.length === 0) {
                return false;
            }

            // 获取已选城市中第一个城市的保质期（作为参考）
            const firstSelectedCityKey = itemSelectedCityIds.value[0];
            const selectedCityGuaranteePeriod = getGuaranteePeriodRule(firstSelectedCityKey);

            // 如果当前城市已被选中或已存在，不禁用
            if (itemSelectedCityIds.value.includes(cityId) || (props.existedIds && props.existedIds.includes(cityId))) {
                return false;
            }

            // 获取该城市的建议保质期
            const cityGuaranteePeriod = getGuaranteePeriodRule(cityId);

            // 如果新城市没有建议保质期，但已选城市有，禁用它
            if (!cityGuaranteePeriod && selectedCityGuaranteePeriod) {
                return true;
            }

            // 如果新城市有建议保质期，但已选城市没有，禁用它
            if (cityGuaranteePeriod && !selectedCityGuaranteePeriod) {
                return true;
            }

            // 如果两者都有建议保质期，检查是否一致
            if (cityGuaranteePeriod && selectedCityGuaranteePeriod) {
                const isMismatch = cityGuaranteePeriod.guaranteePeriod !== selectedCityGuaranteePeriod.guaranteePeriod
                    || cityGuaranteePeriod.guaranteePeriodType !== selectedCityGuaranteePeriod.guaranteePeriodType;
                if (isMismatch) {
                    return true;
                }
            }

            return false;
        };

        /**
         * 将接口返回的城市数据转换为 mc-level-select 需要的树形结构
         * 结构：区域 -> 经营城市（跳过基盘城市层级）
         *
         * @param {Array} data - 接口返回的原始数据
         * @returns {Array} 转换后的树形数据
         */
        const transformData = (data) => {
            if (!Array.isArray(data) || data.length === 0) {
                return [];
            }

            const resultList = [];

            // 遍历，将所有区域提升到顶层
            data.forEach((ytItem) => {
                (ytItem.areaConfigList || []).forEach((areaItem) => {
                    resultList.push({
                        // 区域层级（顶层）
                        id: `area_${areaItem.areaId}`,
                        label: areaItem.areaName,
                        children: [...(areaItem.manageCityList || [])].sort((a, b) => {
                            // 主城（type === 1）置顶
                            if (a.type === 1 && b.type !== 1) return -1;
                            if (a.type !== 1 && b.type === 1) return 1;
                            // type 相同时按经营城市ID升序
                            return a.manageCityId - b.manageCityId;
                        }).map((manageCity) => {
                            const label = manageCity.manageCityName;

                            const cityId = manageCity.manageCityId;

                            // 综合判断城市是否禁用
                            let isDisabled = (props.disabledCityIds || []).includes(cityId);

                            // optionCity 兼容：已存在的城市禁用
                            if (props.existedIds && props.existedIds.includes(cityId)) {
                                isDisabled = true;
                            }

                            // optionCity 兼容：isPbCityDisabledIds 中的城市禁用
                            if (props.isPbCityDisabledIds && props.isPbCityDisabledIds.includes(cityId)) {
                                isDisabled = true;
                            }

                            // optionCity 兼容：保质期一致性检查
                            const hasGuaranteePeriodRule = props.guaranteePeriodMap && (
                                (props.guaranteePeriodMap instanceof Map && props.guaranteePeriodMap.size > 0)
                                || (!(props.guaranteePeriodMap instanceof Map) && Object.keys(props.guaranteePeriodMap).length > 0)
                            );
                            if (props.title === '保质期' && hasGuaranteePeriodRule) {
                                isDisabled = isDisabled || isCityDisabledByGuaranteePeriod(cityId);
                            }

                            return {
                                // 城市ID作为唯一标识
                                id: cityId,
                                label,
                                // 保留原始数据字段用于tab筛选
                                cityStatus: manageCity.cityStatus || 1,
                                type: manageCity.type, // 1: 主城，2: 卫星城
                                manageCityId: manageCity.manageCityId, // 保留原始城市ID
                                // 标记禁用状态
                                disabled: isDisabled,
                            };
                        }),
                    });
                });
            });

            // 对区域列表按区域ID升序排列
            resultList.sort((a, b) => a.areaId - b.areaId);

            return resultList;
        };

        // 合并所有需要显示的城市ID集合
        const allTargetCityIds = computed(() => {
            const ids = [...new Set([
                ...(props.existedIds || []),
                ...(props.publicIds || []),
                ...(props.selectedIds || []),
            ])];
            return ids.length > 0 ? new Set(ids) : null; // null 表示显示全集
        });

        /**
         * 目标城市 ID 集合的稳定 key，用于判断「仅选择变化」与「可选集合变化」
         * 选择某城市后 publicIds 少一个、selectedIds 多一个，并集不变，key 不变，避免 innerData 换引用导致选择器重置到第一个区域/基盘城市
         * 同时考虑 disabledCityIds 的变化，以便在禁用状态改变时更新 innerData
         */
        const targetIdsKey = computed(() => {
            const set = allTargetCityIds.value;
            const targetKey = set ? [...set].sort().join(',') : '';
            const disabledKey = (props.disabledCityIds || []).sort().join(',');
            return `${targetKey}|${disabledKey}`;
        });

        /**
         * 过滤树形数据，只保留目标城市ID相关的节点
         * 树形结构：区域 -> 经营城市（无基盘城市层级）
         * @param {Array} data - 转换后的树形数据
         * @returns {Array} 过滤后的树形数据
         */
        const filterTreeData = (data) => {
            // 如果没有指定目标城市，返回全集
            if (!allTargetCityIds.value) {
                return data;
            }

            const targetIds = allTargetCityIds.value;

            // 过滤树形结构，保留包含目标城市的分支
            return data.map((areaNode) => {
                // 直接过滤经营城市层（areaNode.children 就是经营城市列表）
                const filteredManageCities = (areaNode.children || []).filter((manageCity) => targetIds.has(manageCity.id));

                // 如果该区域下有目标经营城市，保留该区域
                if (filteredManageCities.length > 0) {
                    return {
                        ...areaNode,
                        children: filteredManageCities,
                    };
                }
                return null;
            }).filter(Boolean); // 移除 null 值
        };

        // 原始树形数据（用于依赖 raw 数据变化）
        const rawData = computed(() => transformData(props.data));

        // 稳定引用的内部展示数据：仅在「目标 ID 集合」或「原始数据」变化时更新，避免仅因选中项变化导致 mc-level-select 收到新 data 引用而重置到第一个区域/基盘城市
        const innerDataRef = ref([]);
        const lastDataDeps = ref({ targetIdsKey: '', rawLen: 0 });
        watch(
            [targetIdsKey, rawData],
            ([idsKey, raw]) => {
                const rawLen = (raw && raw.length) || 0;
                if (idsKey === lastDataDeps.value.targetIdsKey && rawLen === lastDataDeps.value.rawLen) {
                    return;
                }
                lastDataDeps.value = { targetIdsKey: idsKey, rawLen };
                innerDataRef.value = filterTreeData(raw, allTargetCityIds.value);
            },
            { immediate: true, deep: true },
        );
        const innerData = computed(() => innerDataRef.value);

        // 城市状态配置
        const CITY_STATUS_CONFIG = [
            { label: '全部城市', status: null },
        ];

        // 计算tab配置：只显示城市状态筛选
        const innerTabConfig = computed(() => {
            const tabs = [];

            // 城市状态筛选
            CITY_STATUS_CONFIG.forEach(({ label, status }) => {
                tabs.push({
                    label,
                    filter: status === null ? () => true : (item) => item?.cityStatus === status,
                });
            });

            return tabs;
        });

        // 监听外部value变化
        watch(
            () => props.value,
            (newVal) => {
                innerValue.value = newVal;
            },
            { deep: true },
        );

        /**
         * 处理选择变化
         */
        const handleChange = (selectedValues) => {
            innerValue.value = selectedValues;
            emit('input', selectedValues);
            emit('change', selectedValues);
            // option-city 兼容
            if (props.uuid) {
                const selectCityIds = selectedValues.filter((id) => !props.existedIds.includes(id));
                emit('update:selected:city', { uuid: props.uuid, selectCityIds });
                emit('selected-ids-change');
            }
        };

        // 是否存在有效的城市，如果不存在，需要将这个item清理（optionCity 兼容）
        const hasEffectiveIds = computed(() => {
            const selectedIds = innerValue.value || [];
            const existedIds = props.existedIds || [];
            const publicIds = props.publicIds || [];
            return selectedIds.length > 0 || existedIds.length > 0 || publicIds.length > 0;
        });

        return {
            innerValue,
            innerData,
            innerTabConfig,
            handleChange,
            hasEffectiveIds,
        };
    },
};
</script>

<style scoped lang="scss">
.basic-plan-city-select {
    width: 100%;
}
</style>
